import { z } from 'zod/v3';

type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.null(),
    z.boolean(),
    z.number(),
    z.string(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

export const runStatusSchema = z.enum([
  'queued',
  'running',
  'posted',
  'failed',
  'skipped',
  'cancelled',
]);

export const severityTierSchema = z.enum(['critical', 'major', 'minor', 'nit']);
export const analyticsBucketSchema = z.enum(['day', 'week', 'month']);
export const confidenceSchema = z.enum(['high', 'medium', 'low']);
export const depthBucketSchema = z.enum(['shallow', 'standard', 'deep']);
export const axisLevelSchema = z.enum(['low', 'medium', 'high']);
export const reviewerInitialContextSchema = z.enum(['full-diff', 'summary']);
export const reviewerToolDisabledReasonSchema = z.enum([
  'repoRootUnavailable',
  'checkoutCapabilityUnavailable',
  'githubReadsUnavailable',
]);

export const runUsageSchema = z
  .object({
    model: z.string(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cost: z.number().nullable(),
  })
  .strict();

export const runSummarySchema = z
  .object({
    id: z.string(),
    repo_full_name: z.string(),
    pr_number: z.number(),
    head_sha: z.string(),
    requested_by: z.string(),
    status: runStatusSchema,
    attempts: z.number(),
    retry_count: z.number(),
    error: z.string().nullable(),
    total_cost: z.number().nullable(),
    comment_count: z.number().nullable(),
    created_at: z.string(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
  })
  .strict();

export const runsResponseSchema = z
  .object({
    runs: z.array(runSummarySchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  })
  .strict();

export const reviewCommentSchema = z
  .object({
    // Stable IDs were added after historical review results had been stored.
    findingId: z.string().optional(),
    file: z.string(),
    line: z.number(),
    severity: severityTierSchema,
    category: z.string(),
    message: z.string(),
    confidence: confidenceSchema.optional(),
    title: z.string().optional(),
    suggestion: z.string().optional(),
    summary: z.string().optional(),
    agent: z.string().optional(),
    agents: z.array(z.string()).optional(),
  })
  .strict();

export const classifierAxesSchema = z
  .object({
    complexity: axisLevelSchema,
    prodImpact: axisLevelSchema,
    interconnectedness: axisLevelSchema,
  })
  .strict();

export const reviewerToolCallDiagnosticSchema = z
  .object({
    turn: z.number(),
    name: z.string(),
  })
  .strict();

export const reviewerToolUnavailableSchema = z
  .object({
    name: z.string(),
    reason: reviewerToolDisabledReasonSchema,
  })
  .strict();

export const reviewerToolDiagnosticsSchema = z
  .object({
    requestedTools: z.array(z.string()),
    enabledTools: z.array(z.string()),
    toolCallCount: z.number(),
    returnedBytes: z.number(),
    budgetExhausted: z.boolean(),
    responseLimitExceeded: z.boolean().optional(),
    contentTruncated: z.boolean().optional(),
    toolCalls: z.array(reviewerToolCallDiagnosticSchema),
    unavailableTools: z.array(reviewerToolUnavailableSchema).optional(),
    disabledReason: reviewerToolDisabledReasonSchema.optional(),
  })
  .strict();

export const classifyResultSchema = z
  .object({
    skills: z.array(z.string()),
    unknown: z.array(z.string()),
    available: z.array(z.string()),
    axes: classifierAxesSchema,
    depth: depthBucketSchema,
    usage: runUsageSchema,
    fallback: z
      .object({
        model: z.string(),
        message: z.string(),
      })
      .strict()
      .optional(),
    toolUse: reviewerToolDiagnosticsSchema.optional(),
  })
  .passthrough();

export const reviewerRunSchema = z
  .object({
    name: z.string(),
    // Historical result JSON can omit usage or contain incomplete persisted
    // blobs. The detail UI displays whatever valid fields are present.
    usage: z.unknown().optional(),
    toolUse: reviewerToolDiagnosticsSchema.optional(),
  })
  .passthrough();

export const reviewerFailureSchema = z
  .object({
    name: z.string(),
    error: z.string(),
    // Historical failures predate per-failure cost accounting.
    cost: z.number().nullable().optional(),
  })
  .strict();

export const runAggregateUsageSchema = z
  .object({
    // Failed runs can contain only totalCost; completed runs include the
    // stage-level breakdown copied from the pipeline result.
    classifier: runUsageSchema.optional(),
    reviewers: z.array(reviewerRunSchema).optional(),
    failures: z.array(reviewerFailureSchema).optional(),
    totalCost: z.number().nullable(),
  })
  .passthrough();

export const reviewRunResultSchema = z
  .object({
    initialContext: reviewerInitialContextSchema.optional(),
    fallbackReason: z.string().optional(),
    comments: z.array(reviewCommentSchema),
    classifier: classifyResultSchema,
    reviewers: z.array(reviewerRunSchema),
    failures: z.array(reviewerFailureSchema),
    dedupe: runUsageSchema.optional(),
    lineCheck: runUsageSchema.optional(),
    summarize: runUsageSchema.optional(),
    totalCost: z.number().nullable(),
    // These fields were added after older run results had already been stored.
    // Defaults keep those historical rows readable while matching the current
    // producer contract for newly created runs.
    repoSkillNames: z.array(z.string()).default([]),
    repoAgentWarnings: z.array(z.string()).default([]),
  })
  // The agentic-review service owns this cross-repository wire contract.
  // Preserve additive producer fields so deploying the service before
  // Backstage does not turn a backwards-compatible addition into an outage.
  .passthrough();

export const reviewRunDecisionSchema = z
  .object({
    review_run_id: z.string(),
    decision_key: z.string(),
    feature_key: z.string(),
    decision_kind: z.literal('rollout'),
    subject_type: z.enum(['repository', 'pull-request']),
    subject: z.string(),
    assigned_variation: z.string(),
    effective_treatment: z.string(),
    evaluation_source: z.string(),
    rule_id: z.string().nullable(),
    hash_attribute: z.string().nullable(),
    suppression_reason: z.string().nullable(),
    assigned_at: z.string(),
    exposed_at: z.string().nullable(),
  })
  .strict();

export const greenlightPolicySourceSchema = z.enum(['global', 'repository']);
export const greenlightEvaluationStatusSchema = z.enum([
  'pass',
  'fail',
  'indeterminate',
]);

export const greenlightRequirementSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    status: greenlightEvaluationStatusSchema,
    description: z.string().min(1),
  })
  .strict();

export const greenlightRuleSchema = z
  .object({
    id: z.string(),
    status: greenlightEvaluationStatusSchema,
    requirements: z.array(greenlightRequirementSchema),
  })
  .strict();

export const greenlightPolicySchema = z
  .object({
    source: greenlightPolicySourceSchema,
    // Optional only for phased compatibility with service versions deployed
    // before immutable policy-file provenance was added.
    fileUrl: z.string().url().nullable().optional(),
    status: greenlightEvaluationStatusSchema,
    rules: z.array(greenlightRuleSchema),
  })
  .strict();

/**
 * Why a pinned policy definition is not shown by the Greenlight evaluation
 * endpoint. The recorded outcome is unaffected by any of these.
 */
export const greenlightPolicyDefinitionUnavailableReasonSchema = z.enum([
  'no-reference',
  'multiple-policies',
  'not-configured',
  'not-found',
  'fetch-failed',
  'invalid',
]);

export const greenlightPolicyDefinitionSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('available') }).strict(),
  z
    .object({
      status: z.literal('unavailable'),
      reason: greenlightPolicyDefinitionUnavailableReasonSchema,
    })
    .strict(),
]);

export const greenlightEvaluationPolicySchema = z
  .object({
    fileUrl: z.string().url().nullable(),
    status: greenlightEvaluationStatusSchema,
    definition: greenlightPolicyDefinitionSchema,
    rules: z.array(greenlightRuleSchema),
  })
  .strict();

/** Recorded Greenlight evaluation joined to the policy definitions it pinned. */
export const greenlightEvaluationSchema = z
  .object({
    policies: z.array(greenlightEvaluationPolicySchema),
  })
  .strict();

const greenlightRunFields = {
  reasonCode: z.string(),
  evaluatedHeadSha: z.string(),
  completedAt: z.string().nullable(),
  githubReviewId: z.number().int().nullable(),
  // Policies were added to the rollout payload after the original Greenlight
  // projection. Default the omitted field for phased service/UI deployments.
  policies: z.array(greenlightPolicySchema).default([]),
};

/** Compact, author-safe Greenlight state associated with a review run. */
export const greenlightRunSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('shadow'),
      outcome: z.enum([
        'would-approve',
        'would-not-approve',
        'could-not-decide',
        'in-progress',
      ]),
      ...greenlightRunFields,
    })
    .strict(),
  z
    .object({
      mode: z.literal('approve'),
      outcome: z.enum([
        'approved',
        'not-eligible',
        'could-not-decide',
        'approval-failed',
        'in-progress',
      ]),
      ...greenlightRunFields,
    })
    .strict(),
]);

export const runDetailSchema = runSummarySchema.extend({
  comment_id: z.string().nullable(),
  trigger_delivery_id: z.string().nullable(),
  posted_review_id: z.string().nullable(),
  max_retries: z.number(),
  custom_config: z.record(z.unknown()),
  result: reviewRunResultSchema.nullable(),
  usage: z.union([runUsageSchema, runAggregateUsageSchema]).nullable(),
  decisions: z.array(reviewRunDecisionSchema),
  // The service sends null when no attempt exists; optional keeps historical
  // service deployments readable during the additive API rollout.
  greenlight: greenlightRunSchema.nullable().optional(),
});

export const sessionStatusSchema = z.enum(['succeeded', 'failed']);

export const agentSessionSummarySchema = z
  .object({
    id: z.string(),
    reviewRunId: z.string(),
    agentName: z.string(),
    attempt: z.number(),
    status: sessionStatusSchema,
    harness: z.literal('pi'),
    harnessVersion: z.string().nullable(),
    rawFormat: z.literal('pi-session-jsonl'),
    rawFormatVersion: z.number(),
    piSessionId: z.string().nullable(),
    inputTokens: z.number().nullable(),
    outputTokens: z.number().nullable(),
    totalTokens: z.number().nullable(),
    cost: z.number().nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export const sessionsResponseSchema = z
  .object({
    sessions: z.array(agentSessionSummarySchema),
  })
  .strict();

const analyticsCountSchema = z.number().int().nonnegative();
const analyticsCostSchema = z.number().nullable();

export const analyticsScopeSchema = z
  .object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    bucket: analyticsBucketSchema,
    repo: z.string().optional(),
    timeZone: z.enum(['UTC', 'Europe/Prague']).optional(),
    workdaysOnly: z.boolean().optional(),
  })
  .strict();

export const analyticsSummarySchema = z
  .object({
    reviewedPrs: analyticsCountSchema,
    reviewRuns: analyticsCountSchema,
    findings: analyticsCountSchema,
    repositories: analyticsCountSchema,
    cost: analyticsCostSchema,
    runsWithUnknownCost: analyticsCountSchema,
  })
  .strict();

export const greenlightAnalyticsSchema = z
  .object({
    evaluations: analyticsCountSchema,
    approved: analyticsCountSchema,
    wouldApprove: analyticsCountSchema,
    notEligible: analyticsCountSchema,
    notEligibleByReason: z
      .object({
        findings: analyticsCountSchema,
        risk: analyticsCountSchema,
        catalog: analyticsCountSchema,
        other: analyticsCountSchema,
      })
      .strict()
      .optional(),
    indeterminate: analyticsCountSchema,
    pending: analyticsCountSchema,
    dismissed: analyticsCountSchema,
  })
  .strict();

export const analyticsPointSchema = z
  .object({
    start: z.string().datetime(),
    reviewedPrs: analyticsCountSchema,
    reviewRuns: analyticsCountSchema,
    findings: analyticsCountSchema,
    cost: analyticsCostSchema,
    runsWithUnknownCost: analyticsCountSchema,
    findingsBySeverity: z
      .object({
        critical: analyticsCountSchema,
        major: analyticsCountSchema,
        minor: analyticsCountSchema,
        nit: analyticsCountSchema,
      })
      .strict(),
    greenlight: greenlightAnalyticsSchema.optional(),
  })
  .strict();

export const severityBreakdownSchema = z
  .object({ severity: severityTierSchema, findings: analyticsCountSchema })
  .strict();

export const repositoryBreakdownSchema = z
  .object({
    repository: z.string(),
    reviewedPrs: analyticsCountSchema,
    reviewRuns: analyticsCountSchema,
    findings: analyticsCountSchema,
    cost: analyticsCostSchema,
    runsWithUnknownCost: analyticsCountSchema,
  })
  .strict();

export const reviewerBreakdownSchema = z
  .object({
    reviewer: z.string(),
    invocations: analyticsCountSchema,
    contributedFindings: analyticsCountSchema,
    cost: analyticsCostSchema,
    invocationsWithUnknownCost: analyticsCountSchema,
  })
  .strict();

export const analyticsOverviewSchema = z
  .object({
    scope: analyticsScopeSchema,
    summary: analyticsSummarySchema,
    // The service and dashboard deploy independently.
    greenlight: greenlightAnalyticsSchema.optional(),
    series: z.array(analyticsPointSchema),
    findingsBySeverity: z.array(severityBreakdownSchema),
    repositories: z.array(repositoryBreakdownSchema),
    reviewers: z.array(reviewerBreakdownSchema),
  })
  .strict();

export const sessionContentBlockSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('text'),
      text: z.string(),
      truncated: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('thinking'),
      text: z.string(),
      redacted: z.boolean().optional(),
      truncated: z.boolean().optional(),
    })
    .strict(),
  z.object({ kind: z.literal('image'), mimeType: z.string() }).strict(),
  z
    .object({
      kind: z.literal('tool-call'),
      toolCallId: z.string(),
      name: z.string(),
      argsJson: z.string(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('tool-result'),
      toolCallId: z.string().optional(),
      toolName: z.string().optional(),
      isError: z.boolean(),
      text: z.string(),
      truncated: z.boolean().optional(),
    })
    .strict(),
]);

export const sessionEntryKindSchema = z.enum([
  'user',
  'assistant',
  'tool-call',
  'tool-result',
  'compaction',
  'metadata',
  'custom',
  'unknown',
]);

export const piSessionEntryViewSchema = z
  .object({
    type: z.string(),
    id: z.string().optional(),
    parentId: z.string().nullable().optional(),
    timestamp: z.string().optional(),
    raw: jsonValueSchema,
    display: z
      .object({
        kind: sessionEntryKindSchema,
        title: z.string().optional(),
      })
      .strict(),
    content: z.array(sessionContentBlockSchema).optional(),
  })
  .strict();

export const piSessionEntryOutputSchema = piSessionEntryViewSchema.omit({
  raw: true,
});

export const sessionUsageSchema = z
  .object({
    inputTokens: z.number().nullable(),
    outputTokens: z.number().nullable(),
    totalTokens: z.number().nullable(),
    cost: z.number().nullable(),
  })
  .strict();

export const reviewSessionViewSchema = z
  .object({
    id: z.string(),
    reviewRunId: z.string(),
    agentName: z.string(),
    status: sessionStatusSchema,
    harness: z.literal('pi'),
    rawFormat: z.literal('pi-session-jsonl'),
    rawFormatVersion: z.number(),
    startedAt: z.string(),
    finishedAt: z.string().optional(),
    error: z.string().optional(),
    usage: sessionUsageSchema,
    entries: z.array(piSessionEntryViewSchema),
  })
  .strict();
